import React, { useRef, useState } from 'react'
import PropTypes from 'prop-types'
import formatMessage from 'format-message'
import DrawerHeader from './DrawerHeader'
import { Button } from 'react-bootstrap'


const DrawerContactUs = ({ coursenum = '', userEmail = '' }) => {
    const hasUser = !!(userEmail && String(userEmail).trim())
    const subjectRef = useRef(null)
    const messageRef = useRef(null)
    const fileRef = useRef(null)

    const [isSubmitting, setIsSubmitting] = useState(false)
    const [responseMessage, setResponseMessage] = useState('')
    const [showResponse, setShowResponse] = useState(false)
    const [copyMessage, setCopyMessage] = useState('')

    React.useEffect(() => {
        const handleDrawerStateChange = (event) => {
            const activeDrawer = event && event.detail && event.detail.activeDrawer
            if (activeDrawer === 'contact' && hasUser && subjectRef.current) {
                setTimeout(() => {
                    if (subjectRef.current) {
                        subjectRef.current.focus()
                    }
                }, 400)
            }
        }

        document.addEventListener('drawer-state-change', handleDrawerStateChange)
        return () => {
            document.removeEventListener('drawer-state-change', handleDrawerStateChange)
        }
    }, [hasUser])

    const handleSubmit = async (e) => {
        e.preventDefault()
        setIsSubmitting(true)
        setShowResponse(false)

        try {
        const subject = subjectRef.current ? subjectRef.current.value : ''
        const message = messageRef.current ? messageRef.current.value : ''
        const file = fileRef.current && fileRef.current.files ? fileRef.current.files[0] : null

        const formDataToSend = new FormData()
        formDataToSend.append('subject', subject)
        formDataToSend.append('message', message)
        if (file) {
            formDataToSend.append('attachment', file)
        }

        const csrfMetaTag = document.querySelector('meta[name="csrf-token"]')
        const csrfInput = document.querySelector('input[name="csrfmiddlewaretoken"]')
        const csrfToken = (csrfMetaTag && csrfMetaTag.getAttribute('content')) ||
                        (csrfInput && csrfInput.value) ||
                        getCookie('csrftoken')

        if (csrfToken) {
            formDataToSend.append('csrfmiddlewaretoken', csrfToken)
        }

        // the first url param is the coursenum -- without it the view can't resolve the course
        const contactUrl = coursenum
            ? `/base_app/navigation.contact_us/${encodeURIComponent(coursenum)}/`
            : '/base_app/navigation.contact_us'

        const response = await fetch(contactUrl, {
            method: 'POST',
            body: formDataToSend,
            headers: {
            'X-CSRFToken': csrfToken
            }
        })

        if (response.ok) {
            setResponseMessage(formatMessage('Your message has been sent successfully. We will get back to you soon.'))
            if (subjectRef.current) subjectRef.current.value = ''
            if (messageRef.current) messageRef.current.value = ''
            if (fileRef.current) fileRef.current.value = ''
        } else {
            setResponseMessage(formatMessage('There was an error sending your message. Please try again.'))
        }
        } catch (error) {
        console.error('Error submitting contact form:', error)
        setResponseMessage(formatMessage('There was an error sending your message. Please try again.'))
        } finally {
        setIsSubmitting(false)
        setShowResponse(true)
        }
    }

    const getCookie = (name) => {
        let cookieValue = null
        if (document.cookie && document.cookie !== '') {
        const cookies = document.cookie.split(';')
        for (let i = 0; i < cookies.length; i++) {
            const cookie = cookies[i].trim()
            if (cookie.substring(0, name.length + 1) === (name + '=')) {
            cookieValue = decodeURIComponent(cookie.substring(name.length + 1))
            break
            }
        }
        }
        return cookieValue
    }

    const copyEmailToClipboard = async () => {
        const email = 'support@myeducator.com'
        try {
            await navigator.clipboard.writeText(email)
            setCopyMessage(formatMessage('Email address copied to clipboard!'))
            setTimeout(() => setCopyMessage(''), 3000)
        } catch (err) {
            // Fallback for older browsers
            const textArea = document.createElement('textarea')
            textArea.value = email
            document.body.appendChild(textArea)
            textArea.select()
            try {
                document.execCommand('copy')
                setCopyMessage(formatMessage('Email address copied to clipboard!'))
                setTimeout(() => setCopyMessage(''), 3000)
            } catch (fallbackErr) {
                setCopyMessage(formatMessage('Unable to copy. Email: support@myeducator.com'))
                setTimeout(() => setCopyMessage(''), 5000)
            }
            document.body.removeChild(textArea)
        }
    }

    return (
        <div className="drawer-contact-container">
        <DrawerHeader title={formatMessage('Contact Us')} />

        <div className="drawer-scrollable-content">
            {!hasUser ? (
                <div className="drawer-empty-state p-3">
                    <p className="text-muted text-p-margin-removed">
                        {formatMessage('For support, please email us at ')}
                        <button
                            type="button"
                            className="btn btn-link p-0 text-primary"
                            style={{ textDecoration: 'underline', fontSize: 'inherit' }}
                            onClick={copyEmailToClipboard}
                            title={formatMessage('Click to copy email address')}
                        >
                            support@myeducator.com
                        </button>
                    </p>
                    {copyMessage && (
                        <p className="text-success text-p-margin-removed mb-2">
                            <i className="fa fa-check mr-1" aria-hidden="true"></i>
                            {copyMessage}
                        </p>
                    )}
                    <p className="text-muted text-p-margin-removed">
                        {formatMessage('You can also find answers to common questions on our ')}
                        <a href="https://myeducator.freshdesk.com/support/home" target="_blank" rel="noopener noreferrer">
                            {formatMessage('support page')}
                        </a>.
                    </p>
                </div>
            ) : (
            <div className="drawer-section">
            <div className="p-2 mb-1">
                <p className="text-p-margin-removed">
                {formatMessage('You can find answers to common questions on our ')}
                <a href="https://myeducator.freshdesk.com/support/home" target="_blank" rel="noopener noreferrer">
                    {formatMessage('support page')}
                </a>.
                </p>
                <p className="text-p-margin-removed">
                {formatMessage('Message will be sent from {email}', { email: userEmail })}
                </p>
            </div>

            {showResponse && (
                <div className="alert alert-info mb-3" role="alert">
                {responseMessage}
                </div>
            )}

            <form onSubmit={handleSubmit} encType="multipart/form-data">
                <div className="form-group mb-3">
                <label htmlFor="contact-subject" className="form-label">
                    {formatMessage('Subject')}
                </label>
                <input
                    type="text"
                    id="contact-subject"
                    name="subject"
                    className="form-control"
                    ref={subjectRef}
                    required
                    disabled={isSubmitting}
                />
                </div>

                <div className="form-group mb-3">
                <label htmlFor="contact-message" className="form-label">
                    {formatMessage('Message')}
                </label>
                <textarea
                    id="contact-message"
                    name="message"
                    className="form-control"
                    rows="6"
                    ref={messageRef}
                    required
                    disabled={isSubmitting}
                />
                </div>

                <div className="form-group mb-3">
                <label htmlFor="contact-file" className="form-label">
                    {formatMessage('Attachment (optional)')}
                </label>
                <input
                    type="file"
                    id="contact-file"
                    name="attachment"
                    className="form-control"
                    ref={fileRef}
                    disabled={isSubmitting}
                />
                <small className="form-text text-muted mt-1">
                    {formatMessage('Maximum file size: 15 MB.')}<br />
                    {formatMessage('For larger files, please email ')}<a href="mailto:support@myeducator.com">support@myeducator.com</a>.
                </small>
                </div>

                <div className="text-left submit-button-container">
                <Button
                    type="submit"
                    className="btn-myed btn-primary btn-primary-myed"
                    disabled={isSubmitting}
                >
                    {isSubmitting ? (
                    <span>
                        <i className="fa fa-spinner fa-spin mr-2" aria-hidden="true"></i>
                        {formatMessage('Sending...')}
                    </span>
                    ) : (
                    formatMessage('Submit')
                    )}
                </Button>
                </div>
            </form>
            </div>
            )}
        </div>
        </div>
    )
}

DrawerContactUs.propTypes = {
    coursenum: PropTypes.string,
    userEmail: PropTypes.string
}

export default DrawerContactUs
